<script setup lang="ts">
import { computed } from 'vue';
import Dialog from 'primevue/dialog';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { useStudyStore } from '@/stores/study';
import { ButtonType, CustomButton, TabHeaderTab, ImportFormChangeTab, ImportStudyConfigTab } from '@/components';
import type { FormConfig } from '@/models';

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const emits = defineEmits(['onClose']);

const closeDialog = () => {
  emits('onClose');
};

const studyStore = useStudyStore();

const getFormChanges = (form: FormConfig) => {
  const fieldUpdates = form.formChanges.fieldUpdates;
  return (
    fieldUpdates.fieldsChanged +
    fieldUpdates.fieldsAdded +
    fieldUpdates.fieldsDeleted +
    Object.keys(form.formChanges.changes).length
  );
};

const getFormErrors = (form: FormConfig) => {
  let errors = 0;
  form.items.forEach(item => {
    errors += item.errors ? Object.keys(item.errors).length : 0;
  });
  return errors;
};

const disableApprove = computed(() => {
  let errors = 0;
  studyStore.studyImportDiff?.forms.forEach(form => errors += getFormErrors(form));
  return Object.keys(studyStore.studyImportDiff!.studyConfigChanges.errors).length > 0 || errors > 0;
});

const approveImport = async () => {
  await studyStore.importAndApproveStudy();
  closeDialog();
};
</script>

<template>
  <div class="dialog-root">
    <Dialog :visible="visible"
      modal
      :header="`Import Summary for ${studyStore.studyImportDiff?.studyConfigChanges.previous.title}`"
      class="dialog"
      :closeOnEscape="true"
      :draggable="false"
      appendTo="self"
      @update:visible="closeDialog">
      <div class="dialog-body">
        <Tabs value="0" scrollable>
          <TabList>
            <Tab value="0">
              <TabHeaderTab title="Study Config"
                :changes="Object.keys(studyStore.studyImportDiff!.studyConfigChanges.changes).length"
                :errors="Object.keys(studyStore.studyImportDiff!.studyConfigChanges.errors).length"/>
            </Tab>
            <Tab v-for="form in studyStore.studyImportDiff!.forms"
              :key="
                form.formChanges.previous.identifier ||
                form.formChanges.changes.identifier
              "
              :value="
                form.formChanges.previous.title ||
                form.formChanges.changes.title || ''
              ">
              <TabHeaderTab :title="
                  form.formChanges.previous.title ||
                  form.formChanges.changes.title ||
                  ''
                "
                :changes="getFormChanges(form)"
                :errors="getFormErrors(form)"/>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel value="0">
              <ImportStudyConfigTab />
            </TabPanel>
            <TabPanel v-for="form in studyStore.studyImportDiff!.forms"
              :key="
                form.formChanges.previous.identifier ||
                form.formChanges.changes.identifier
              "
              :value="
                form.formChanges.previous.title ||
                form.formChanges.changes.title || ''
              ">
              <ImportFormChangeTab :formConfig="form" />
            </TabPanel>
          </TabPanels>
        </Tabs>

        <div class="footer">
          <div className="bottom-buttons" v-if="disableApprove">
            <span>Fix the issues to approve these changes.</span>
          </div>
          <CustomButton text="Approve & Import"
            :disabled="disableApprove"
            :loading="studyStore.importExportLoader.isLoading"
            @onClick="approveImport"
            :type="ButtonType.POSITIVE"/>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-dialog) {
  width: 80vw;
  height: 90%;
}
:deep(.p-dialog-content), .dialog-body {
  height: 100%;
  overflow: hidden;
}
:deep(.p-tabs) {
  height: 90%;
}
:deep(.p-tablist){
  display: flex;
  border-bottom: 1px solid #e2e8f0;
}
:deep(.p-tabpanels) {
  height: 100%;
  padding: 14px 0px;
  overflow: auto;
}
.footer {
  display: flex;
  flex-direction: column;
  align-items: end;
  margin-top: 16px;
}
</style>
